import React, { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'

export interface MSOption { value: string; label: string }

const Checkbox: React.FC<{ on: boolean }> = ({ on }) => (
  <div className={`h-3.5 w-3.5 mr-2 rounded border flex items-center justify-center flex-shrink-0 ${on ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
    {on && <Check className="h-2.5 w-2.5 text-white" />}
  </div>
)

interface Props {
  label: string
  options: MSOption[]
  selected: string[]
  onChange: (v: string[]) => void
}

/** Compact checkbox multi-select dropdown (matches the account picker style). */
const MultiSelectDropdown: React.FC<Props> = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const toggle = (v: string) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v])
  const btnLabel = selected.length === 0 ? `All ${label}` : `${label}: ${selected.length}`

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between gap-1.5 px-3 py-1.5 text-xs border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px]"
      >
        <span className="truncate">{btnLabel}</span>
        <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute left-0 z-20 mt-1 w-56 max-h-72 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg py-1">
          <button type="button" onClick={() => onChange([])} className="w-full flex items-center px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
            <Checkbox on={selected.length === 0} /><span className="truncate">All {label}</span>
          </button>
          {options.map((o) => (
            <button key={o.value} type="button" onClick={() => toggle(o.value)} className="w-full flex items-center px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
              <Checkbox on={selected.includes(o.value)} /><span className="truncate">{o.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default MultiSelectDropdown
